import { useState, useEffect } from "react";

import { ArrowBack, Add, Delete } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  TextField,
  Typography,
  Paper,
  Box,
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton,
} from "@mui/material";

export interface PublicContact {
  name?: string;
  email?: string;
  phone_number?: string;
}

export interface GroupFormData {
  name: string;
  description: string;
  isPublic: boolean;
  requiresAdmittanceModeration: boolean;
  publicContacts: PublicContact[];
}

interface AlertGroupFormProps {
  mode: "create" | "update";
  initialData?: GroupFormData;
  loading: boolean;
  error: string;
  onSubmit: (data: GroupFormData) => void;
  onBack: () => void;
  title: string;
  subtitle: string;
  submitButtonText: string;
  submitButtonLoadingText: string;
}

const AlertGroupForm = ({
  mode,
  initialData,
  loading,
  error,
  onSubmit,
  onBack,
  title,
  subtitle,
  submitButtonText,
  submitButtonLoadingText,
}: AlertGroupFormProps) => {
  const [groupName, setGroupName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [isPublic, setIsPublic] = useState<boolean>(false);
  const [requiresAdmittanceModeration, setRequiresAdmittanceModeration] =
    useState<boolean>(false);
  const [publicContacts, setPublicContacts] = useState<PublicContact[]>([
    { name: "", email: "", phone_number: "" },
  ]);

  useEffect(() => {
    if (initialData) {
      setGroupName(initialData.name);
      setDescription(initialData.description);
      setIsPublic(initialData.isPublic);
      setRequiresAdmittanceModeration(initialData.requiresAdmittanceModeration);
      if (initialData.publicContacts.length > 0) {
        setPublicContacts(initialData.publicContacts);
      }
    }
  }, [initialData]);

  const handleSubmit = () => {
    onSubmit({
      name: groupName,
      description,
      isPublic,
      requiresAdmittanceModeration,
      publicContacts,
    });
  };

  const handleContactChange = (
    index: number,
    field: keyof PublicContact,
    value: string,
  ) => {
    const updatedContacts = [...publicContacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      [field]: value,
    };
    setPublicContacts(updatedContacts);
  };

  const addContact = () => {
    setPublicContacts([
      ...publicContacts,
      { name: "", email: "", phone_number: "" },
    ]);
  };

  const removeContact = (index: number) => {
    if (publicContacts.length > 1) {
      const updatedContacts = publicContacts.filter((_, i) => i !== index);
      setPublicContacts(updatedContacts);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !loading) {
      handleSubmit();
    }
  };

  return (
    <div className="users_list_container">
      <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
        <div id="top-page-div"></div>

        <Button
          onClick={onBack}
          startIcon={<ArrowBack />}
          sx={{ mb: 3 }}
          disabled={loading}
        >
          Back
        </Button>

        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            {title}
          </Typography>

          <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
            {subtitle}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              onKeyDown={handleKeyDown}
              required
              disabled={loading}
              autoFocus={mode === "create"}
              variant="outlined"
              error={!!error && !isPublic && error.includes("name")}
              helperText="Enter a name for the group"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={loading}
              variant="outlined"
              multiline
              rows={3}
              helperText="Optional description for the group"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Make this group public"
            />
            <Typography variant="body2" sx={{ ml: 4, color: "text.secondary" }}>
              Public groups can be accessed by anyone with the group link
            </Typography>
          </Box>

          {isPublic && (
            <Box sx={{ mb: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Public Contact Information
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 2, color: "text.secondary" }}
              >
                Provide at least one contact method for public inquiries
              </Typography>
              {publicContacts.map((contact, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    position: "relative",
                  }}
                >
                  {publicContacts.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeContact(index)}
                      disabled={loading}
                      sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <TextField
                      label="Contact Name"
                      value={contact.name || ""}
                      onChange={(e) =>
                        handleContactChange(index, "name", e.target.value)
                      }
                      disabled={loading}
                      variant="outlined"
                      size="small"
                    />
                    <TextField
                      label="Email"
                      type="email"
                      value={contact.email || ""}
                      onChange={(e) =>
                        handleContactChange(index, "email", e.target.value)
                      }
                      disabled={loading}
                      variant="outlined"
                      size="small"
                    />
                    <TextField
                      label="Phone Number"
                      value={contact.phone_number || ""}
                      onChange={(e) =>
                        handleContactChange(
                          index,
                          "phone_number",
                          e.target.value,
                        )
                      }
                      disabled={loading}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Box>
              ))}
              <Button
                startIcon={<Add />}
                onClick={addContact}
                disabled={loading}
                variant="outlined"
                size="small"
              >
                Add Another Contact
              </Button>
              {error && isPublic && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {error}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={requiresAdmittanceModeration}
                  onChange={(e) =>
                    setRequiresAdmittanceModeration(e.target.checked)
                  }
                  disabled={loading}
                />
              }
              label="Require admittance moderation"
            />
            <Typography variant="body2" sx={{ ml: 4, color: "text.secondary" }}>
              When enabled, new bulletins submitted to this group require
              approval
            </Typography>
          </Box>

          {error && !isPublic && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading || !groupName.trim()}
              size="large"
              startIcon={
                loading ? <CircularProgress size={20} color="inherit" /> : null
              }
            >
              {loading ? submitButtonLoadingText : submitButtonText}
            </Button>

            <Button
              variant="outlined"
              onClick={onBack}
              disabled={loading}
              size="large"
            >
              Cancel
            </Button>
          </Box>
        </Paper>
      </Box>
    </div>
  );
};

export default AlertGroupForm;
